import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, GraduationCap } from "lucide-react";

function splitLabel(label) {
  const text = String(label || "").trim();
  if (!text) return { title: "Course", meta: "" };
  const parts = text.split(" · ");
  if (parts.length < 2) return { title: text, meta: "" };
  return {
    title: parts[0].trim(),
    meta: parts.slice(1).join(" · ").trim(),
  };
}

/**
 * Compact course picker for multi-enrolled students.
 * Hidden when the student has 0–1 courses.
 */
export default function StudentCourseFilter({
  options = [],
  value = "all",
  onChange,
  showFilter = true,
  loading = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();

  const items = useMemo(() => {
    const mapped = options.map((opt) => {
      const id = String(opt.admissionId || opt.key);
      const { title, meta } = splitLabel(opt.label);
      return { id, title, meta, rawLabel: opt.label };
    });
    return [
      { id: "all", title: "All courses", meta: "", rawLabel: "All courses" },
      ...mapped,
    ];
  }, [options]);

  const selected = useMemo(() => {
    const current = String(value || "all");
    return items.find((item) => item.id === current) || items[0];
  }, [items, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!showFilter || options.length <= 1) return null;

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
  };

  const triggerText = selected?.meta
    ? `${selected.title} · ${selected.meta}`
    : selected?.title || "All courses";

  return (
    <div
      ref={rootRef}
      className={`ems-student-course-filter${open ? " is-open" : ""}${
        className ? ` ${className}` : ""
      }`}
    >
      <span className="ems-student-course-filter-icon" aria-hidden="true">
        <GraduationCap size={13} strokeWidth={2.25} />
      </span>
      <span className="ems-student-course-filter-label">Course</span>
      <button
        type="button"
        className="ems-student-course-filter-trigger"
        disabled={loading}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        title={triggerText}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="ems-student-course-filter-value">{triggerText}</span>
        <ChevronDown
          className="ems-student-course-filter-chevron"
          size={14}
          strokeWidth={2.25}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          id={listId}
          className="ems-student-course-filter-menu"
          role="listbox"
          aria-label="Filter by course"
        >
          <ul className="ems-student-course-filter-list">
            {items.map((item) => {
              const active = item.id === selected?.id;
              const label = item.meta
                ? `${item.title} · ${item.meta}`
                : item.title;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`ems-student-course-filter-option${
                      active ? " is-active" : ""
                    }`}
                    title={label}
                    onClick={() => pick(item.id)}
                  >
                    <span className="ems-student-course-filter-option-text">
                      {label}
                    </span>
                    {active ? (
                      <Check size={13} strokeWidth={2.5} aria-hidden="true" />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
